// Procedural-body indexing orchestrator — ProceduralBodyIndexing PRD Slice 0.3.
//
// Mirrors `indexViewListing` in shape: builds a parsed buffer directly from
// `listing.bodyText` (no prefix-stripping; procedural bodies start at
// `CREATE PROCEDURE` / `CREATE TRIGGER` and the universal scanner already
// splits them into per-statement chunks via the dialect-aware delimiter
// state machine). The buffer is handed to `walkProceduralTree` which routes
// any embedded SELECTs through the existing `walkSelect` + column-ref
// scanner pipeline.
//
// On parse failure or zero embedded SELECTs the orchestrator emits a single
// `parse-failed` placeholder spanning the whole body, identical in shape to
// the view/TVF parse-failed path. Slice 27's rename planner uses that
// placeholder to refuse rewriting bodies whose semantics it can't trust.

import { buildParsedDocumentFromSource, getDocumentStructure } from '../../../sql-grammar/document';
import { buildLineIndex, rangeFromOffsets, type LineIndex } from '../../../sql-grammar/document/lineIndex';
import type { ParseOptions, SqlEngine } from '../../../sql-grammar/model';
import type { ParsedBuffer } from '../../../sql-resolver/types';

import type { IdentifierIndex, ReferenceRecord } from '../../types';
import type { PersistedObjectListing } from '../objectBodyFetcher';

import { walkProceduralTree } from './walkProceduralTree';

export function indexProceduralListing(
  listing: PersistedObjectListing,
  index: IdentifierIndex,
): void {
  const lineIdx = buildLineIndex(listing.bodyText);
  const engine = listing.parseOptions.engine;
  const parseOptions: ParseOptions = {
    engine,
    identifierFolding: defaultFoldingForEngine(engine),
  };

  let buffer: ParsedBuffer;
  try {
    const parsedDocument = buildParsedDocumentFromSource(listing.bodyText, parseOptions);
    const documentStructure = getDocumentStructure(parsedDocument, listingDocumentId(listing));
    buffer = { parsedDocument, documentStructure };
  } catch {
    recordParseFailed(listing, lineIdx, index);
    return;
  }

  const walked = walkProceduralTree(
    buffer,
    listing.bodyText,
    parseOptions,
    listing,
    0,
    lineIdx,
    index,
  );
  if (!walked) recordParseFailed(listing, lineIdx, index);
}

function listingDocumentId(listing: PersistedObjectListing): string {
  const { foldedSchema, foldedName } = listing.objectName;
  return `${listing.objectKind}::${foldedSchema ?? ''}::${foldedName}`;
}

function defaultFoldingForEngine(engine: SqlEngine): ParseOptions['identifierFolding'] {
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}

function recordParseFailed(
  listing: PersistedObjectListing,
  lineIdx: LineIndex,
  index: IdentifierIndex,
): void {
  const range = rangeFromOffsets(lineIdx, 0, listing.bodyText.length);
  const record: ReferenceRecord = {
    objectKind: listing.objectKind,
    objectName: listing.objectName,
    identifier: listing.objectName,
    identifierKind: 'routine',
    range,
    referenceKind: 'read',
    parseStatus: 'parse-failed',
  };
  index.record(record);
}
